import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { subscriptionService } from '@/services/subscriptionService';
import { DEFAULT_APP_PLATFORM } from '@/const/platformAccess';

export interface SubscriptionErrorData {
  error: string;
  message: string;
  action: string;
  action_url: string;
  status?: string;
  subscription?: { status?: string } | null;
}

interface AccessStatusResponse {
  has_access: boolean;
  platforms?: string[];
  error?: string;
  message?: string;
  action?: string;
  action_url?: string;
  status?: string;
  subscription?: { status?: string } | null;
  access_expires_at?: string | null;
  product_purchase_id?: number;
  product_name?: string;
}

export type CheckStatusOptions = {
  /** When true, do not flip hasAccess to true if the request fails (e.g. on /permission-denied). */
  preserveStateOnNetworkError?: boolean;
};

const SUBSCRIPTION_DEFAULTS = {
  error: 'subscription_required',
  message: 'Subscription required to access this resource.',
  action: 'Subscribe Now',
  action_url: '/subscription'
} as const;

/** How long (ms) a successful checkStatus result is considered fresh in the router guard. */
const STATUS_CACHE_TTL_MS = 60_000;

export const useSubscriptionAccessStore = defineStore('subscriptionAccess', () => {
  const hasAccess = ref(true);
  const isChecking = ref(false);
  const error = ref<SubscriptionErrorData | null>(null);
  /** Product codes from GET /subscriptions/access/status/ (e.g. campus, offers). */
  const platforms = ref<string[]>([]);
  /** When access comes from a product purchase, ISO expiry timestamp. */
  const accessExpiresAt = ref<string | null>(null);
  const productName = ref<string | null>(null);
  /**
   * `has_access` is true but this app's platform code is missing from `platforms`.
   * Not the same as no subscription (`!hasAccess`).
   */
  const platformAccessDenied = ref(false);
  /** True after the first successful checkStatus call. */
  const isHydrated = ref(false);
  /** Timestamp of the last successful checkStatus call. */
  const lastCheckedAt = ref(0);

  // Computed properties
  const hasError = computed(() => error.value !== null);

  const message = computed(() => error.value?.message || '');

  const actionButtonText = computed(
    () => error.value?.action || SUBSCRIPTION_DEFAULTS.action
  );

  const actionUrl = computed(
    () => error.value?.action_url || SUBSCRIPTION_DEFAULTS.action_url
  );

  const errorDetails = computed(() => {
    if (!error.value) return null;

    return {
      message: error.value.message,
      status: error.value.status || error.value.subscription?.status || null,
      action: error.value.action,
      actionUrl: error.value.action_url
    };
  });

  const hasPlatform = (code: string) => platforms.value.includes(code);

  function syncPlatformAccessDenied() {
    platformAccessDenied.value = hasAccess.value && !hasPlatform(DEFAULT_APP_PLATFORM);
  }

  function applyAccessStatusPayload(data: AccessStatusResponse) {
    hasAccess.value = data.has_access ?? true;
    platforms.value = Array.isArray(data.platforms) ? data.platforms : [];
    accessExpiresAt.value = data.access_expires_at ?? null;
    productName.value = data.product_name ?? null;

    if (!hasAccess.value) {
      platformAccessDenied.value = false;
      setError({
        error: data.error || SUBSCRIPTION_DEFAULTS.error,
        message: data.message || SUBSCRIPTION_DEFAULTS.message,
        action: data.action || SUBSCRIPTION_DEFAULTS.action,
        action_url: data.action_url || SUBSCRIPTION_DEFAULTS.action_url,
        status: data.status,
        subscription: data.subscription ?? null
      });
    } else {
      clearError();
      syncPlatformAccessDenied();
    }
  }

  const checkStatus = async (
    throwOnError = true,
    options?: CheckStatusOptions
  ): Promise<boolean> => {
    isChecking.value = true;

    try {
      const response = await subscriptionService.checkAccessStatus();
      applyAccessStatusPayload(response.data);

      isHydrated.value = true;
      lastCheckedAt.value = Date.now();

      return hasAccess.value;
    } catch (err) {
      console.error('Failed to check subscription access status:', err);

      if (!options?.preserveStateOnNetworkError) {
        // If API call fails, assume access is granted to avoid blocking users.
        // The actual subscription check will happen on backend for protected routes.
        hasAccess.value = true;
      }

      if (throwOnError) {
        throw err;
      }

      return hasAccess.value;
    } finally {
      isChecking.value = false;
    }
  };

  /** Returns true if cached status is still fresh and a new API call can be skipped. */
  const isCacheFresh = () =>
    isHydrated.value && Date.now() - lastCheckedAt.value < STATUS_CACHE_TTL_MS;

  const setError = (errorData: SubscriptionErrorData) => {
    hasAccess.value = false;
    error.value = errorData;
    platformAccessDenied.value = false;
  };

  const clearError = () => {
    hasAccess.value = true;
    error.value = null;
    platformAccessDenied.value = false;
  };

  return {
    // State
    hasAccess,
    isChecking,
    error,
    platforms,
    accessExpiresAt,
    productName,
    platformAccessDenied,
    isHydrated,
    lastCheckedAt,

    // Computed
    hasError,
    message,
    actionButtonText,
    actionUrl,
    errorDetails,

    // Methods
    checkStatus,
    isCacheFresh,
    setError,
    clearError,
    hasPlatform
  };
});
